import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emendaService, { type EmendaDTO } from '../services/emendaService';
import { getErrorMessage } from '../services/api';
import funcaoOrcamentariaService from '../services/funcaoOrcamentariaService';
import { useAuth } from '../context/AuthContext';
import { Permission, UserRole } from '../types/user.types';
import DashboardPagination from '../components/DashboardPagination';
import EmendasFilterBar from '../components/EmendasFilterBar';
import StatusBadge, { type BadgeTone } from '../components/ui/StatusBadge';
import { useEmendasFilter, CAMARA_AGUARDANDO } from '../hooks/useEmendasFilter';
import { useEmendaRoleFlags } from '../hooks/useEmendaRoleFlags';
import { fMoney } from '../utils/emendaFormatters';
import { emendaEtapa, ETAPAS } from '../utils/emendaEtapa';
import { UI_THEME } from '../ui/theme';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const classificacaoLabel = (value?: string | number): string | null => {
  const normalized = value == null ? '' : String(value);
  if (normalized === '1' || /custeio/i.test(normalized)) return 'Custeio';
  if (normalized === '2' || /investimento/i.test(normalized)) return 'Investimento';
  return normalized || null;
};

const etapaTone = (index: number, devolvida: boolean): BadgeTone =>
  devolvida ? 'danger' : index >= 5 ? 'success' : index === 0 ? 'neutral' : 'info';

const DashboardEmendasPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { hasRole, user, isAdmin, isGestor, isVereador, isWorkflow, canFilterByVereador } = useEmendaRoleFlags();

  const canCreateEmenda = hasPermission(Permission.CREATE_EMENDA);

  const [emendas, setEmendas] = useState<EmendaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [funcoesMap, setFuncoesMap] = useState<Record<string, string>>({});

  useEffect(() => {
    funcaoOrcamentariaService.list()
      .then((list) => {
        const map: Record<string, string> = {};
        (list || []).forEach((funcao) => {
          if (funcao.codigo) map[funcao.codigo] = funcao.descricao;
        });
        setFuncoesMap(map);
      })
      .catch(() => setFuncoesMap({}));
  }, []);

  useEffect(() => {
    const fetchEmendas = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await emendaService.listWithDetails();
        setEmendas(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(getErrorMessage(err, 'Não foi possível carregar as emendas.'));
        setEmendas([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEmendas();
  }, []);

  const {
    query, setQuery,
    statusFilter, setStatusFilter,
    etapaFilter, setEtapaFilter,
    year, setYear,
    areaFilter, setAreaFilter,
    esferaFilter, setEsferaFilter,
    transferenciaFilter, setTransferenciaFilter,
    camaraFilter, setCamaraFilter,
    detailFilter, setDetailFilter,
    vereador, setVereador,
    secretaria, setSecretaria,
    myQueueOnly, setMyQueueOnly,
    page, setPage,
    pageSize, setPageSize,
    filtered,
    paginatedEmendas,
    myQueueCount,
    myQueueStatuses,
    isQueueRestricted,
    clearFilters,
  } = useEmendasFilter(emendas, { hasRole, user, isAdmin, isGestor, defaultPageSize: 10 });

  const vereadores = useMemo(() => {
    const names = new Set<string>();
    emendas.forEach((emenda) => { if (emenda.councilorName) names.add(emenda.councilorName); });
    return Array.from(names).sort();
  }, [emendas]);

  const secretarias = useMemo(() => {
    const names = new Set<string>();
    emendas.forEach((emenda) => { if (emenda.secretariaDestino) names.add(emenda.secretariaDestino); });
    return Array.from(names).sort();
  }, [emendas]);

  const anos = useMemo(() => {
    const years = new Set<string>();
    emendas.forEach((emenda) => {
      const value = emenda.exercicio ? String(emenda.exercicio) : (emenda.date || '').slice(0, 4);
      if (value) years.add(value);
    });
    return Array.from(years).sort().reverse();
  }, [emendas]);

  const areaOptions = useMemo(() => {
    const options = new Map<string, string>();
    Object.entries(funcoesMap).forEach(([codigo, descricao]) => {
      if (codigo) options.set(codigo, descricao || codigo);
    });
    emendas.forEach((emenda) => {
      if (emenda.funcaoCodigo && !options.has(emenda.funcaoCodigo)) options.set(emenda.funcaoCodigo, emenda.funcaoCodigo);
      if (emenda.category && !options.has(emenda.category)) options.set(emenda.category, emenda.category);
    });
    return Array.from(options, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [emendas, funcoesMap]);

  const etapaCounts = useMemo(() => {
    const counts = [0, 0, 0, 0, 0, 0];
    filtered.forEach((emenda) => {
      counts[emendaEtapa(emenda).index] += 1;
    });
    return counts;
  }, [filtered]);

  const devolvidasCount = useMemo(
    () => filtered.filter((emenda) => emendaEtapa(emenda).devolvida || (emenda.status || '').toLocaleLowerCase('pt-BR').includes('devolv')).length,
    [filtered],
  );

  // Emendas reprovadas que voltaram ao Orçamento e aguardam a devolução formal à Câmara.
  const aguardandoDevolucaoCamara = useMemo(
    () => emendas.filter((emenda) => /^retornada ao or/i.test(emenda.statusCicloVida ?? '')).length,
    [emendas],
  );

  const summaryCards = [
    { label: 'Total exibido', value: filtered.length, dot: 'bg-slate-400' },
    { label: 'Recebido', value: etapaCounts[0], dot: 'bg-blue-600' },
    { label: 'Admissibilidade', value: etapaCounts[1], dot: 'bg-amber-500' },
    { label: 'Demanda', value: etapaCounts[2], dot: 'bg-teal-500' },
    { label: 'Documental', value: etapaCounts[3], dot: 'bg-violet-500' },
    { label: 'Execucao', value: etapaCounts[4], dot: 'bg-sky-600' },
    { label: 'Concluido', value: etapaCounts[5], dot: 'bg-emerald-500' },
    { label: 'Devolvidas', value: devolvidasCount, dot: 'bg-red-500' },
  ];

  const saasColumns = useMemo(() => {
    const groups = ETAPAS.map((label, index) => ({ label, index, items: [] as EmendaDTO[] }));
    filtered.forEach((emenda) => {
      const etapa = emendaEtapa(emenda);
      groups[etapa.index]?.items.push(emenda);
    });
    return groups.filter((group) => group.items.length > 0 || ['Recebido', 'Demanda', 'Documental', 'Em execução'].includes(group.label));
  }, [filtered]);

  const isSaas = UI_THEME === 'saas';

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-2xl border p-6 shadow-sm ${
        isSaas
          ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/50 to-indigo-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
          : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-sky-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
      }`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>
              {isSaas ? 'Workspace' : 'Gestao'}
            </p>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-100">Emendas</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {isSaas
                  ? 'Acompanhe emendas por etapa, priorize a fila de trabalho e abra os processos quando precisar dos detalhes completos.'
                  : 'Acompanhe o ciclo das emendas, filtre sua fila de trabalho e abra rapidamente os detalhes de cada processo.'}
              </p>
            </div>
          </div>

          {canCreateEmenda && (
            <button
              type="button"
              onClick={() => navigate('/dashboard/cadastro-emenda')}
              className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm ${
                isSaas ? 'bg-teal-600 hover:bg-teal-700' : 'bg-[#1351B4] hover:bg-[#0B3D8F]'
              }`}
            >
              + Nova Emenda
            </button>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-4 xl:grid-cols-8 dark:border-slate-700 dark:bg-slate-700">
          {summaryCards.map((card) => (
            <div key={card.label} className={`${isSaas ? 'bg-white/90 dark:bg-slate-800/80' : 'bg-white dark:bg-slate-800'} px-4 py-3`}>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${card.dot}`} />
                <p className="truncate text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">{card.label}</p>
              </div>
              <p className="mt-2 text-2xl font-bold tabular-nums text-slate-950 dark:text-slate-100">{card.value}</p>
            </div>
          ))}
        </div>
      </section>

      {(isAdmin || hasRole(UserRole.ORCAMENTO)) && aguardandoDevolucaoCamara > 0 && (() => {
        const isFiltrando = camaraFilter === CAMARA_AGUARDANDO;
        return (
          <button
            type="button"
            onClick={() => setCamaraFilter(isFiltrando ? '' : CAMARA_AGUARDANDO)}
            aria-pressed={isFiltrando}
            className={`flex w-full items-start gap-3 rounded-2xl border px-5 py-4 text-left shadow-sm transition ${
              isFiltrando
                ? 'border-amber-400 bg-amber-100 ring-2 ring-amber-300 dark:border-amber-600 dark:bg-amber-900/40'
                : 'border-amber-300 bg-amber-50 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20'
            }`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div className="min-w-0 flex-1 text-sm text-amber-800 dark:text-amber-200">
              <p className="font-semibold">
                {aguardandoDevolucaoCamara} {aguardandoDevolucaoCamara === 1 ? 'emenda aguarda' : 'emendas aguardam'} devolução à Câmara
              </p>
              <p className="mt-0.5 text-xs leading-5">
                Reprovadas na análise e retornadas ao Orçamento. {isFiltrando ? 'Mostrando apenas essas — clique para limpar o filtro.' : 'Clique para filtrar a lista e ver quais são; abra cada uma e use “Devolver à Câmara”.'}
              </p>
            </div>
            <span className="mt-0.5 shrink-0 rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 dark:border-amber-700 dark:bg-slate-900 dark:text-amber-200">
              {isFiltrando ? 'Limpar' : 'Filtrar'}
            </span>
          </button>
        );
      })()}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
        <EmendasFilterBar
          query={query}
          onQueryChange={setQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={(value) => { setStatusFilter(value); setMyQueueOnly(false); }}
          etapaFilter={etapaFilter}
          onEtapaFilterChange={(value) => { setEtapaFilter(value); setMyQueueOnly(false); }}
          detailFilter={detailFilter}
          onDetailFilterChange={setDetailFilter}
          year={year}
          onYearChange={setYear}
          areaFilter={areaFilter}
          onAreaFilterChange={setAreaFilter}
          areas={areaOptions}
          transferencia={transferenciaFilter}
          onTransferenciaChange={setTransferenciaFilter}
          esfera={esferaFilter}
          onEsferaChange={setEsferaFilter}
          camara={camaraFilter}
          onCamaraChange={setCamaraFilter}
          anos={anos}
          vereador={vereador}
          onVereadorChange={setVereador}
          vereadores={vereadores}
          canFilterByVereador={canFilterByVereador}
          secretaria={secretaria}
          onSecretariaChange={setSecretaria}
          secretarias={secretarias}
          canFilterBySecretaria={canFilterByVereador}
          myQueueOnly={myQueueOnly}
          onMyQueueToggle={() => { setMyQueueOnly((value) => !value); setStatusFilter('Todas'); setEtapaFilter('Todas'); }}
          myQueueCount={myQueueCount}
          myQueueStatuses={myQueueStatuses}
          isQueueRestricted={isQueueRestricted}
          onClearFilters={clearFilters}
          variant="dashboard"
        />
      </section>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
          Carregando emendas...
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm dark:border-red-800 dark:bg-red-900/20">
          <p className="font-semibold text-red-800 dark:text-red-300">Erro ao carregar emendas</p>
          <p className="mt-2 text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
          <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {isQueueRestricted
              ? 'Nenhuma emenda na sua fila no momento.'
              : emendas.length === 0
                ? isWorkflow
                  ? 'Nenhuma emenda pendente para o seu perfil no momento.'
                  : 'Nenhuma emenda cadastrada ainda.'
                : 'Nenhuma emenda encontrada com os filtros selecionados.'}
          </p>
          {canCreateEmenda && emendas.length === 0 && (
            <button
              type="button"
              onClick={() => navigate('/dashboard/cadastro-emenda')}
              className="mt-5 inline-flex items-center justify-center rounded-xl bg-[#1351B4] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B3D8F]"
            >
              Criar primeira emenda
            </button>
          )}
        </div>
      ) : isSaas ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Quadro por etapa</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Abra uma emenda para ver o objeto completo e demais detalhes.</p>
            </div>
            <div className="flex gap-2">
              {['Minha fila', 'Todas visiveis', 'Devolvidas', 'Concluidas'].map((view, index) => (
                <span
                  key={view}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                    index === 1
                      ? 'bg-teal-600 text-white'
                      : 'border border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                  }`}
                >
                  {view}
                </span>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto pb-2">
            <div className="flex min-w-max gap-4">
              {saasColumns.map((column) => (
                <div key={column.label} className="w-80 shrink-0 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{column.label}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">{column.items.length}</span>
                  </div>

                  <div className="space-y-3">
                    {column.items.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-xs text-slate-400 dark:border-slate-700">
                        Nenhuma emenda nesta etapa
                      </div>
                    ) : column.items.map((emenda) => {
                      const etapa = emendaEtapa(emenda);
                      const area = emenda.funcaoCodigo ? (funcoesMap[emenda.funcaoCodigo] || emenda.funcaoCodigo) : (emenda.category || '-');
                      const objeto = emenda.objectDetail || emenda.description || 'Objeto nao informado';

                      return (
                        <button
                          key={emenda.id}
                          type="button"
                          onClick={() => navigate(`/dashboard/emendas/${emenda.id}`)}
                          className="block w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 dark:hover:border-teal-500"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-bold text-slate-950 dark:text-slate-100">{emenda.officialCode || 'Sem codigo'}</div>
                              {emenda.exercicio && <div className="text-xs font-medium text-slate-500 dark:text-slate-400">{emenda.exercicio}</div>}
                            </div>
                            <div className="text-right text-sm font-bold tabular-nums text-slate-950 dark:text-slate-100">{fMoney(emenda.value)}</div>
                          </div>

                          <div className="mt-3 font-semibold uppercase leading-snug text-slate-800 dark:text-slate-100">
                            {emenda.institutionName || 'Instituicao nao informada'}
                          </div>
                          {emenda.councilorName && (
                            <div className="mt-1 text-sm">
                              <span className="font-medium text-slate-500 dark:text-slate-400">Parlamentar: </span>
                              <span className="font-semibold text-teal-700 dark:text-teal-300">{emenda.councilorName}</span>
                            </div>
                          )}
                          <p className="text-truncate-multiline mt-2 text-sm leading-5 text-slate-600 dark:text-slate-400">{objeto}</p>

                          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap gap-2">
                              <StatusBadge tone={etapaTone(etapa.index, etapa.devolvida)}>
                                {etapa.devolvida ? `${etapa.label} devolvida` : etapa.label}
                              </StatusBadge>
                              <span className="rounded-md border border-slate-200 px-2 py-0.5 text-[0.7rem] font-semibold uppercase text-slate-500 dark:border-slate-700 dark:text-slate-300">
                                {area}
                              </span>
                            </div>
                            <span className="text-xs font-semibold text-teal-700 dark:text-teal-300">Ver emenda completa</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
          <div className="overflow-x-auto">
            <table className="min-w-[1120px] table-fixed">
              <colgroup>
                <col className="w-[12%]" />
                <col className="w-[24%]" />
                <col className="w-[30%]" />
                <col className="w-[12%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[40px]" />
              </colgroup>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-400">
                  <th className="px-4 py-3">Processo</th>
                  <th className="px-4 py-3">Beneficiario / Parlamentar</th>
                  <th className="px-4 py-3">Objeto</th>
                  <th className="px-4 py-3">Etapa</th>
                  <th className="px-4 py-3">Area</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-2 py-3" aria-label="Abrir" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {paginatedEmendas.map((emenda) => {
                  const etapa = emendaEtapa(emenda);
                  const classif = classificacaoLabel(emenda.classificacao);
                  const area = emenda.funcaoCodigo ? (funcoesMap[emenda.funcaoCodigo] || emenda.funcaoCodigo) : (emenda.category || null);
                  const objeto = emenda.objectDetail || emenda.description;

                  return (
                    <tr
                      key={emenda.id}
                      onClick={() => navigate(`/dashboard/emendas/${emenda.id}`)}
                      className="group cursor-pointer bg-white align-top transition even:bg-slate-50 hover:bg-sky-50 dark:bg-transparent dark:even:bg-slate-900/50 dark:hover:bg-slate-700/50"
                    >
                      <td className="px-4 py-4">
                        <div className="font-bold text-slate-950 dark:text-slate-100">{emenda.officialCode || 'Sem codigo'}</div>
                        {emenda.exercicio && (
                          <div className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">{emenda.exercicio}</div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {emenda.tipoTransferencia && <StatusBadge tone="info">{emenda.tipoTransferencia}</StatusBadge>}
                          {classif && <StatusBadge tone="neutral">{classif}</StatusBadge>}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="font-semibold uppercase leading-snug text-slate-800 dark:text-slate-100">
                          {emenda.institutionName || 'Instituicao nao informada'}
                        </div>
                        {emenda.councilorName && (
                          <div className="mt-1 text-sm leading-5">
                            <span className="font-medium text-slate-500 dark:text-slate-400">Parlamentar: </span>
                            <span className="font-semibold text-sky-800 dark:text-sky-300">{emenda.councilorName}</span>
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <p className="text-truncate-multiline text-sm leading-5 text-slate-600 dark:text-slate-400">
                          {objeto || 'Objeto nao informado'}
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        <StatusBadge tone={etapaTone(etapa.index, etapa.devolvida)}>
                          {etapa.devolvida ? `${etapa.label} devolvida` : etapa.label}
                        </StatusBadge>
                      </td>

                      <td className="px-4 py-4">
                        <span className="block truncate text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          {area || '-'}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <span className="tabular-nums font-bold text-slate-950 dark:text-slate-100">{fMoney(emenda.value)}</span>
                      </td>

                      <td className="px-2 py-4">
                        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-300">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DashboardPagination
            page={page}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            itemLabel="emendas"
          />
        </section>
      )}
    </div>
  );
};

export default DashboardEmendasPage;
